import { Product, ProductBillingPeriod } from "@/types/product"
import { formatCurrency } from "@/composables/formatters"

export function getCancelURL(product: Product): string {
  return getURLByKind(product, "cancel")
}
export function getDecrementURL(product: Product): string {
  return getURLByKind(product, "dec")
}
export function getPurchaseURL(product: Product): string {
  return getURLByKind(product, "purchase")
}
export function getSignupURL(product: Product): string {
  return getURLByKind(product, "signup")
}
function getURLByKind(product: Product, kind: string): string {
  if (!product.productRequestWorkflows) return ""

  const workflow = product.productRequestWorkflows.find(
    (prw) => prw.kind === kind
  )

  return workflow?.next ? workflow.next || "" : ""
}

export function getProductSavingsDisplay(product: Product): {
  title: string
  value: string
  defaultValue: string
} {
  const title = product.savingsTitle || "Your Savings"
  const value = product.savingsValue

  const signupTiers = product.productTiers.filter(
    (productTier) => !productTier.addon && productTier.subscribable
  )
  if (signupTiers.length === 0) {
    return {
      title: title,
      value: value,
      defaultValue: value,
    }
  }

  // get the maximum savings when there are multiple tiers.
  const productTier =
    signupTiers.length === 1
      ? signupTiers[0]
      : signupTiers.reduce((maxTier, tier) => {
          const maxTierSavings = product.included
            ? maxTier.retailPrice
            : maxTier.retailPrice - maxTier.memberPrice
          const tierSavings = product.included
            ? tier.retailPrice
            : tier.retailPrice - tier.memberPrice
          if (maxTierSavings > tierSavings) {
            return maxTier
          } else {
            return tier
          }
        }, signupTiers[0])

  const savings = product.included
    ? productTier.retailPrice
    : productTier.retailPrice - productTier.memberPrice

  // avoid displaying 0 savings
  if (savings <= 0) {
    return {
      title: title,
      value: value,
      defaultValue: "",
    }
  }

  let savingsDisplay = formatCurrency(savings)
  const billingPeriod = getBillingPeriodPerLabel(productTier.billingPeriod)
  if (billingPeriod !== "") {
    savingsDisplay = `${savingsDisplay}/${billingPeriod}`
  }

  const defaultValue =
    signupTiers.length > 1 ? `Up to ${savingsDisplay}` : savingsDisplay

  return {
    title: title,
    value: value || defaultValue,
    defaultValue: defaultValue,
  }
}

/**
 * Get the product tier billing period "per" unit display label.
 * @param billingPeriod ProductBillingPeriod
 * @returns string
 */
export function getBillingPeriodPerLabel(billingPeriod: ProductBillingPeriod) {
  switch (billingPeriod) {
    case "hourly":
      return "hour"
    case "monthly":
      return "month"
    case "yearly":
      return "year"
  }

  return ""
}
